/**
 * SafeBoundary — Error Boundary minimale per isolare i moduli
 * (es. PreventivoForm) dai crash di React 19 (`insertBefore`,
 * `removeChild`) durante portali/Toaster/download. Se l'errore
 * si manifesta, mostra un fallback INLINE senza mai distruggere
 * la chrome dell'app o cacciare l'utente fuori dal modulo.
 *
 * Note: gli ErrorBoundary devono essere class component.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

export default class SafeBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[SafeBoundary:${this.props.label || "unknown"}] caught:`, error);
    // eslint-disable-next-line no-console
    console.error(`[SafeBoundary:${this.props.label || "unknown"}] componentStack:`, info?.componentStack);
    this.setState({ info });
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="border border-amber-300 bg-amber-50 rounded-md p-4 text-sm" data-testid="safe-boundary-fallback">
        <div className="flex items-center gap-2 font-medium text-amber-900 mb-1">
          <AlertCircle className="h-4 w-4" />
          Si è verificato un errore temporaneo nel modulo
        </div>
        <p className="text-amber-800 mb-3">
          I tuoi dati non sono stati persi. Clicca "Ripristina" per continuare,
          oppure "Indietro" per tornare alla galleria.
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={this.reset} data-testid="safe-boundary-retry">Ripristina</Button>
          {this.props.onBack && (
            <Button size="sm" variant="outline" onClick={this.props.onBack}>
              Indietro
            </Button>
          )}
        </div>
        {this.state.error?.message && (
          <pre className="mt-3 text-[10px] text-amber-700 whitespace-pre-wrap" data-testid="safe-boundary-error-message">
            {`[${this.props.label || "unknown"}]\n${String(this.state.error.message)}`}
            {this.state.info?.componentStack ? `\n\nComponent stack:${this.state.info.componentStack.slice(0, 800)}` : ""}
          </pre>
        )}
      </div>
    );
  }
}
